import { Users, AlertTriangle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Displays who else is viewing/editing an entity
 */
export const PresenceIndicator = ({ othersViewing = [], className = '' }) => {
  if (!othersViewing || othersViewing.length === 0) return null;

  const firstUser = othersViewing[0];
  const additionalCount = othersViewing.length - 1;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md ${className}`}>
            <Users className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800">
              <strong>{firstUser.user_name}</strong>
              {additionalCount > 0 && ` +${additionalCount} other${additionalCount > 1 ? 's' : ''}`}
              {' '}also viewing
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">Currently viewing:</p>
            {othersViewing.map((user, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="text-xs capitalize">
                  {user.user_role}
                </Badge>
                <span>{user.user_name}</span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground mt-2">
              Changes you make may overwrite their work, or vice versa.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Alert banner for version conflicts
 */
export const VersionConflictAlert = ({ 
  onRefresh, 
  message = "This record was modified by another user.",
  className = '' 
}) => {
  return (
    <div className={`flex items-center justify-between gap-4 p-4 bg-destructive/10 border border-destructive/30 rounded-lg ${className}`}>
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
        <div>
          <p className="font-medium text-destructive">Conflict Detected</p>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 text-sm font-medium whitespace-nowrap"
        >
          Refresh & Reload
        </button>
      )}
    </div>
  );
};

export default PresenceIndicator;
